import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { GMB } from "../../services/gmbApiConfig";

// Fetches all the images for a store
export const fetchStoreImages = createAsyncThunk(
  "gmbImage/fetchStoreImages",
  async ({ account_id, location_id }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${GMB.ENDPOINTS.IMAGES.GET_UPLOADED_PRESIGNED_URLS}`,
        { account_id, location_id },
        {
          headers: {
            Authorization: Cookies.get("ssp_sso_token"),
            "Content-Type": "application/json",
          },
        },
      );

      const allImages = Object.values(response.data)?.filter(
        (url) => url.startsWith("http") || url.startsWith("https"),
      );

      return allImages;
    } catch (error) {
      return rejectWithValue("Failed to fetch store images");
    }
  },
);

//Generates a presigned url to upload images to S3
export const getPresignedUrl = createAsyncThunk(  
  "gmbImage/getPresignedUrl",
  async ({ account_id, location_id }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${GMB.ENDPOINTS.IMAGES.GET_PRESIGNED_URL_TO_UPLOAD}`,
        { account_id, location_id },
        {
          headers: {
            Authorization: Cookies.get("ssp_sso_token"),
            "Content-Type": "application/json",
          },
        },
      );
      if(response.status!==200){
        return rejectWithValue(response.data.error_message)
      }
      return response.data;
    } catch (error) {
      return rejectWithValue("Failed to get presigned URL");
    }
  },
);

// Uploads the image to S3 (presigned url)
export const uploadImageToS3 = createAsyncThunk(
  "gmbImage/uploadImageToS3",
  async ({ presignedUrl, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      Object.entries(presignedUrl.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append("file", file);

      const response = await axios.post(presignedUrl.url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.status === 204;
    } catch (error) {
      return rejectWithValue("Failed to upload image to S3");
    }
  },
);

// Uploadds the S3 image to GMB and returns the status
export const uploadImageToGMB = createAsyncThunk(
  "gmbImage/uploadImageToGMB",
  async ({ account_id, location_id, key, category }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${GMB.ENDPOINTS.IMAGES.UPLOAD}`,
        {
          account_id,
          location_id,
          key,
          category,
          media_format: "PHOTO",
        },
        {
          headers: {
            Authorization: Cookies.get("ssp_sso_token"),
            "Content-Type": "application/json",
          },
        },
      );
      if(response.status!==200){
        toast.error("Please contact Admin")
        return rejectWithValue(response.data.error_message)
      }
      return response.data;
    } catch (error) {
      return rejectWithValue("Failed to upload image to GMB");
    }
  },
);

// Deletes the images from the S3 and GMB
export const deleteImages = createAsyncThunk(
  "gmbImage/deleteImages",
  async ({ account_id, location_id, selectedImages }, { rejectWithValue }) => {
    try {
      const extractedText = selectedImages
        .map((url) => {
          const match = url.match(/(prod|uat)\/[^.]+\.jpg/);
          return match ? match[0] : null;
        })
        .filter(Boolean);

      const response = await axios.post(
        `${GMB.ENDPOINTS.IMAGES.DELETE_PHOTOS}`,
        {
          account_id,
          location_id,
          s3_keys: extractedText,
        },
        {
          headers: {
            Authorization: Cookies.get("ssp_sso_token"),
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue("Failed to delete images");
    }
  },
);

const initialState = {
  images: [],
  presignedUrl: null,
  selectedImages: [],
  isDeleteMode: false,
  loading: {
    images: false,
    upload: false,
    delete: false,
  },
  error: null,
  modalState: {
    isOpen: false,
    currentImageIndex: 0,
  },
  uploadState: {
    category: "ADDITIONAL",
    imagePreview: "",
    imageFile: null,
    isModalOpen: false,
  },
};

const gmbImageSlice = createSlice({
  name: "gmbImage",
  initialState,
  reducers: {
    setSelectedImages: (state, action) => {
      state.selectedImages = action.payload;
    },
    setDeleteMode: (state, action) => {
      state.isDeleteMode = action.payload;
      if (!action.payload) {
        state.selectedImages = [];
      }
    },
    setModalState: (state, action) => {
      state.modalState = { ...state.modalState, ...action.payload };
    },
    setUploadState: (state, action) => {
      state.uploadState = { ...state.uploadState, ...action.payload };
    },
    resetUploadState: (state) => {
      state.uploadState = initialState.uploadState;
    },
    clearErrors: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Store Images
      .addCase(fetchStoreImages.pending, (state) => {
        state.loading.images = true;
      })
      .addCase(fetchStoreImages.fulfilled, (state, action) => {
        state.loading.images = false;
        state.images = action.payload;
      })
      .addCase(fetchStoreImages.rejected, (state, action) => {
        state.loading.images = false;
        state.error = action.payload;
        toast.error("Failed to fetch images");
      })

      // Get Presigned URL
      .addCase(getPresignedUrl.fulfilled, (state, action) => {
        state.presignedUrl = action.payload;
      })
      .addCase(getPresignedUrl.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Upload Image to S3
      .addCase(uploadImageToS3.pending, (state) => {
        state.loading.upload = true;
      })
      .addCase(uploadImageToS3.fulfilled, (state) => {
        // state.loading.upload = false;
      })
      .addCase(uploadImageToS3.rejected, (state, action) => {
        state.loading.upload = false;
        state.error = action.payload;
        toast.error("Failed to upload image");
      })

      // Upload Image to GMB

      .addCase(uploadImageToGMB.fulfilled, (state, action) => {
        if (action.payload?.status === "success") {
          toast.success("Image uploaded successfully");
        }
        state.loading.upload = false;
        state.uploadState = initialState.uploadState;
      })
      .addCase(uploadImageToGMB.rejected, (state, action) => {
        state.loading.upload = false;
        state.error = action.payload;
        toast.error("Failed to upload image to GMB");
      })

      // Delete Images
      .addCase(deleteImages.pending, (state) => {
        state.loading.delete = true;
      })
      .addCase(deleteImages.fulfilled, (state) => {
        state.loading.delete = false;
        state.isDeleteMode = false;
        state.selectedImages = [];
        toast.success("Images deleted successfully");
      })
      .addCase(deleteImages.rejected, (state, action) => {
        state.loading.delete = false;
        state.error = action.payload;
        toast.error("Failed to delete images");
      });
  },
});

// Actions
export const {
  setSelectedImages,
  setDeleteMode,
  setModalState,
  setUploadState,
  resetUploadState,
  clearErrors,
} = gmbImageSlice.actions;

// Selectors
export const selectImages = (state) => state.gmbImage.images;
export const selectSelectedImages = (state) => state.gmbImage.selectedImages;
export const selectIsDeleteMode = (state) => state.gmbImage.isDeleteMode;
export const selectLoading = (state) => state.gmbImage.loading;
export const selectModalState = (state) => state.gmbImage.modalState;
export const selectUploadState = (state) => state.gmbImage.uploadState;
export const selectPresignedUrl = (state) => state.gmbImage.presignedUrl;

export default gmbImageSlice.reducer;
